class PRO2OProductFeatures {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.tabsNav = this.section.querySelector('[data-tabs-nav]');
    this.imageContainer = this.section.querySelector('[data-image-container]');
    this.tabImages = new Map();
    this.tabButtons = [];
    this.featureImg = null;

    const tabsContainer = this.section.querySelector('[data-features-tabs]');
    this.enableTransition = tabsContainer?.dataset.enableImageTransition === 'true';

    this.init();
  }

  init() {
    if (!this.section) {
      console.warn('PROH2O Features: Section element not found');
      return;
    }

    this.buildTabsFromData();

    if (this.tabButtons.length === 0) {
      console.warn('PROH2O Features: No tab data found');
      return;
    }

    this.buildTabImageMap();
    this.preloadImages();
    this.bindEvents();
    this.setupKeyboardNavigation();

    // Render the first tab's image without animation
    this.displayImage(this.tabButtons[0], false);
  }

  buildTabsFromData() {
    const tabDataElements = this.section.querySelectorAll('.feature-tab-data');

    if (!tabDataElements.length || !this.tabsNav) {
      return;
    }

    tabDataElements.forEach((dataEl, index) => {
      const tabId = dataEl.dataset.tabId;
      const title = dataEl.dataset.tabTitle || `Feature ${index + 1}`;
      const description = dataEl.dataset.tabDescription || '';
      const imageUrl = dataEl.dataset.tabImage || '';
      const imageAlt = dataEl.dataset.tabImageAlt || 'Feature image';
      const imageWidth = dataEl.dataset.tabImageWidth || '600';
      const imageHeight = dataEl.dataset.tabImageHeight || '500';
      const imageRadius = dataEl.dataset.tabImageRadius || '8';

      const button = document.createElement('button');
      button.className = `tab-button${index === 0 ? ' active' : ''}`;
      button.setAttribute('data-tab', tabId);
      button.setAttribute('data-image-url', imageUrl);
      button.setAttribute('data-image-alt', imageAlt);
      button.setAttribute('data-image-width', imageWidth);
      button.setAttribute('data-image-height', imageHeight);
      button.setAttribute('data-image-radius', imageRadius);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

      const titleEl = document.createElement('h3');
      titleEl.className = 'tab-title';
      titleEl.textContent = title;

      // Wrap richtext in an inner div so the description can animate via
      // grid-template-rows: 0fr → 1fr (needs exactly one collapsible child).
      const descEl = document.createElement('div');
      descEl.className = 'tab-description';
      const descInner = document.createElement('div');
      descInner.className = 'tab-description-inner';
      descInner.innerHTML = description;
      descEl.appendChild(descInner);

      button.appendChild(titleEl);
      button.appendChild(descEl);

      this.tabsNav.appendChild(button);
      this.tabButtons.push(button);
    });
  }

  buildTabImageMap() {
    this.tabButtons.forEach(button => {
      const tabId = button.getAttribute('data-tab');
      const imageUrl = button.getAttribute('data-image-url');
      const imageAlt = button.getAttribute('data-image-alt');
      const imageWidth = button.getAttribute('data-image-width');
      const imageHeight = button.getAttribute('data-image-height');
      const imageRadius = button.getAttribute('data-image-radius');

      if (tabId) {
        this.tabImages.set(tabId, {
          url: imageUrl || null,
          alt: imageAlt || 'Feature showcase',
          width: imageWidth || '600',
          height: imageHeight || '500',
          radius: imageRadius || '8'
        });
      }
    });
  }

  preloadImages() {
    // Warm the browser cache so later tab clicks don't hit the network.
    this.tabImages.forEach(({ url }) => {
      if (!url) return;
      const preload = new Image();
      preload.src = url;
    });
  }

  ensureImageElement() {
    if (this.featureImg && this.featureImg.isConnected) return this.featureImg;

    this.imageContainer.innerHTML = '';
    const img = document.createElement('img');
    img.className = 'feature-image';
    img.loading = 'eager';
    img.decoding = 'async';
    this.imageContainer.appendChild(img);
    this.featureImg = img;
    return img;
  }

  displayImage(button, animate = this.enableTransition) {
    if (!this.imageContainer) return;

    const tabId = button.getAttribute('data-tab');
    const imageData = this.tabImages.get(tabId);

    if (!imageData?.url) {
      this.featureImg = null;
      this.imageContainer.innerHTML = '<div class="placeholder-image"><span>No image available</span></div>';
      return;
    }

    const img = this.ensureImageElement();
    const applyImage = () => {
      if (img.src !== imageData.url) img.src = imageData.url;
      img.alt = imageData.alt;
      img.width = imageData.width;
      img.height = imageData.height;
      img.style.borderRadius = `${imageData.radius}px`;
    };

    if (animate) {
      // Restart the CSS keyframe animation by removing the class,
      // forcing a synchronous reflow, then re-adding it.
      this.imageContainer.classList.remove('is-swapping');
      void this.imageContainer.offsetWidth;
      applyImage();
      this.imageContainer.classList.add('is-swapping');
    } else {
      applyImage();
    }
  }

  bindEvents() {
    this.tabButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleTabClick(e, button));
    });
  }

  handleTabClick(event, button) {
    event.preventDefault();

    this.updateActiveStates(button);
    this.displayImage(button);

    this.section.dispatchEvent(new CustomEvent('tabchange', {
      detail: {
        activeTab: button.getAttribute('data-tab'),
        tabTitle: button.querySelector('.tab-title')?.textContent
      }
    }));
  }

  updateActiveStates(activeButton) {
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });

    activeButton.classList.add('active');
    activeButton.setAttribute('aria-selected', 'true');
  }

  setupKeyboardNavigation() {
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('keydown', (e) => {
        this.handleKeyNavigation(e, index);
      });
    });
  }

  handleKeyNavigation(event, currentIndex) {
    let newIndex;

    switch(event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (currentIndex + 1) % this.tabButtons.length;
        this.tabButtons[newIndex].focus();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;
        this.tabButtons[newIndex].focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.tabButtons[currentIndex].click();
        break;
      case 'Home':
        event.preventDefault();
        this.tabButtons[0].focus();
        break;
      case 'End':
        event.preventDefault();
        this.tabButtons[this.tabButtons.length - 1].focus();
        break;
    }
  }

  activateTab(tabId) {
    const targetButton = this.section.querySelector(`[data-tab="${tabId}"]`);
    if (targetButton) {
      targetButton.click();
    }
  }

  getActiveTab() {
    const activeButton = this.section.querySelector('.tab-button.active');
    return activeButton?.getAttribute('data-tab') || null;
  }

  destroy() {
    this.tabButtons.forEach(button => {
      button.removeEventListener('click', this.handleTabClick);
      button.removeEventListener('keydown', this.handleKeyNavigation);
    });
  }
}

function initializePROH2OFeatures() {
  const sections = document.querySelectorAll('.pvl-proh2o-features-section');

  sections.forEach(section => {
    if (!section.dataset.initialized) {
      new PRO2OProductFeatures(section);
      section.dataset.initialized = 'true';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePROH2OFeatures);
} else {
  initializePROH2OFeatures();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.pvl-proh2o-features-section');
    if (section) {
      section.dataset.initialized = 'false';
      new PRO2OProductFeatures(section);
      section.dataset.initialized = 'true';
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.pvl-proh2o-features-section');
    if (section) {
      section.dataset.initialized = 'false';
    }
  });
}

window.PRO2OProductFeatures = PRO2OProductFeatures;
